import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api-client';
import { useAuth } from '@/lib/auth-context';
import { Server, Plus, CreditCard, BookOpen, Activity, DollarSign, MessageSquare, ArrowUpRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function Dashboard() {
  const { user } = useAuth();
  const [instances, setInstances] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.getInstances().then(setInstances);
    api.getAnalytics().then(setAnalytics);
  }, []);

  const running = instances.filter(i => i.status === 'running').length;

  return (
    <div data-testid="dashboard-overview" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono">Dashboard</h1>
          <p className="text-sm text-zinc-500 font-mono">Welcome back, {user?.fullName?.split(' ')[0] || 'User'}</p>
        </div>
        <Link to="/dashboard/instances/create" data-testid="dashboard-create-btn" className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-5 py-2.5 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)] flex items-center gap-2">
          <Plus size={16} /> New Instance
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Active Instances', value: `${running}/${instances.length}`, icon: Server, color: 'text-emerald-400' },
          { label: 'Monthly Cost', value: `$${analytics?.totalCost?.toFixed(2) || '0.00'}`, icon: DollarSign, color: 'text-cyan-400' },
          { label: 'Messages Today', value: analytics?.totalMessages?.toLocaleString() || '0', icon: MessageSquare, color: 'text-emerald-400' },
          { label: 'Avg Uptime', value: `${analytics?.avgUptime || 0}%`, icon: Activity, color: 'text-cyan-400' },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{s.label}</span>
                <Icon size={16} className={s.color} />
              </div>
              <span className="text-2xl font-mono font-bold text-white">{s.value}</span>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { to: '/dashboard/instances/create', label: 'Create Instance', icon: Plus, desc: 'Deploy a new OpenClaw agent' },
          { to: '/dashboard/billing/credits', label: 'Add Credits', icon: CreditCard, desc: 'Purchase credit packages' },
          { to: '/docs', label: 'View Docs', icon: BookOpen, desc: 'Getting started guides' },
        ].map((a, i) => {
          const Icon = a.icon;
          return (
            <Link key={i} to={a.to} data-testid={`quick-action-${i}`} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 hover:border-emerald-500/20 transition-all group flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-all">
                <Icon size={18} className="text-emerald-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-mono text-white font-bold">{a.label}</h3>
                <p className="text-xs text-zinc-600">{a.desc}</p>
              </div>
              <ArrowUpRight size={14} className="text-zinc-600 group-hover:text-emerald-400 transition-colors" />
            </Link>
          );
        })}
      </div>

      {/* Instances + Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
          <h2 className="text-sm font-mono text-zinc-400 uppercase tracking-widest mb-4">Instances</h2>
          <div className="space-y-3">
            {instances.map(inst => (
              <Link key={inst.id} to={`/dashboard/instances/${inst.id}`} data-testid={`instance-card-${inst.id}`} className="flex items-center gap-4 p-3 rounded-sm hover:bg-white/[0.02] transition-all group">
                <div className={`w-2 h-2 rounded-full ${inst.status === 'running' ? 'bg-emerald-500' : 'bg-zinc-600'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono text-white truncate">{inst.name}</p>
                  <p className="text-xs text-zinc-600 font-mono">{inst.tier} &middot; {inst.region}</p>
                </div>
                <span className={`text-xs font-mono px-2 py-0.5 rounded-sm ${inst.status === 'running' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>{inst.status}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
          <h2 className="text-sm font-mono text-zinc-400 uppercase tracking-widest mb-4">Cost Trend (30d)</h2>
          {analytics?.dailyMessages && (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={analytics.dailyMessages}>
                <defs>
                  <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="#27272A" tick={{ fill: '#52525B', fontSize: 10, fontFamily: 'JetBrains Mono' }} />
                <YAxis stroke="#27272A" tick={{ fill: '#52525B', fontSize: 10, fontFamily: 'JetBrains Mono' }} />
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #27272A', borderRadius: 2, fontFamily: 'JetBrains Mono', fontSize: 12 }} />
                <Area type="monotone" dataKey="cost" stroke="#10B981" fill="url(#costGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
